import React from "react";
import { Storefront, WarningCircle } from "@phosphor-icons/react";

import {
  Empty,
  LoadingBlock,
  Spinner,
  Stat,
  StatRow,
  Switch,
  SwitchRow,
} from "../../../design-system/components/desktop";
import {
  StatChip,
  StatStrip,
  SwitchRow as DsSwitchRow,
} from "../../../design-system/components/mobile";
import { useViewport } from "../../../design-system/hooks/useViewport";
import {
  Panel,
  PanelAction,
  PanelFacts,
} from "../../../design-system/patterns/settings";
import { errorText, LOAD_FAILURE } from "../../../utils/errorText";

/**
 * Store details. Pure: the availability mutation moved up to `Profile.jsx`.
 *
 * A SWITCH COMMITS IMMEDIATELY, which is why this section has no save button
 * and why the availability toggle sits nowhere near one. The pending spinner
 * beside it is the whole feedback mechanism.
 *
 * The three stat tiles used to be `.stat-tile.green`, `.blue` and `.purple`,
 * three hardcoded decorative colours on three numbers that mean nothing to each
 * other. They are now plain `Stat` tiles: the value carries the information and
 * the colour was never carrying any.
 *
 * The store logo is rendered at its own size rather than in a fixed 120px box
 * with an `/store-logo.jpg` placeholder standing in for "no logo". A missing
 * logo is now stated.
 */
const StoreDetailsSection = ({
  sellerInfo,
  isLoading,
  error,
  storeUnavailable = false,
  availabilityLoading = false,
  onToggleAvailability,
  onEdit,
}) => {
  const { isDesktop } = useViewport();
  const stats = sellerInfo?.stats;

  const statTiles = [
    { label: "Positive rating", value: stats?.positive_rating ?? "Not rated yet" },
    { label: "Items sold", value: stats?.items_sold ?? 0 },
    { label: "Average ship time", value: stats?.avg_ship_time ?? "No data yet" },
  ];

  return (
    <Panel
      icon={Storefront}
      title="Store details"
      action={<PanelAction onClick={onEdit}>Edit store info</PanelAction>}
    >
      {isLoading ? (
        <LoadingBlock>Loading your store</LoadingBlock>
      ) : error ? (
        <Empty
          icon={WarningCircle}
          illustration="request-failed"
          tone="error"
          title="We could not load your store"
          text={errorText(error, LOAD_FAILURE)}
          inline
        />
      ) : (
        <>
          <div className="pfx-availability">
            {isDesktop ? (
              <SwitchRow
                title="Store availability"
                sub={
                  storeUnavailable
                    ? "Your store is hidden from buyers."
                    : "Your store is open to buyers."
                }
              >
                <Switch
                  checked={!storeUnavailable}
                  disabled={availabilityLoading}
                  onChange={(e) => onToggleAvailability(!e.target.checked)}
                />
              </SwitchRow>
            ) : (
              <DsSwitchRow
                label="Store availability"
                sub={
                  storeUnavailable
                    ? "Your store is hidden from buyers."
                    : "Your store is open to buyers."
                }
                checked={!storeUnavailable}
                disabled={availabilityLoading}
                onCheckedChange={(next) => onToggleAvailability(!next)}
              />
            )}
            {availabilityLoading ? (
              <Spinner size="sm" label="Updating store availability" />
            ) : null}
          </div>

          {sellerInfo ? (
            <>
              <PanelFacts
                items={[
                  { label: "Store name", value: sellerInfo.store_name },
                  { label: "Shipping policy", value: sellerInfo.shipping_policy },
                ]}
              />

              {sellerInfo.store_logo ? (
                <figure className="pfx-logo">
                  <img src={sellerInfo.store_logo} alt={`${sellerInfo.store_name} logo`} />
                  <figcaption>Store logo</figcaption>
                </figure>
              ) : null}

              {/* Three numbers, two tracks, two genuinely different components:
                  desktop is a fixed grid of tiles, mobile is the peek-scroll
                  strip that tells the thumb there is more to the right. */}
              {stats ? (
                isDesktop ? (
                  <StatRow className="pfx-stats">
                    {statTiles.map((tile) => (
                      <Stat key={tile.label} label={tile.label} value={tile.value} />
                    ))}
                  </StatRow>
                ) : (
                  <StatStrip className="pfx-statstrip">
                    {statTiles.map((tile) => (
                      <StatChip key={tile.label} label={tile.label} value={tile.value} />
                    ))}
                  </StatStrip>
                )
              ) : null}
            </>
          ) : null}
        </>
      )}
    </Panel>
  );
};

export default StoreDetailsSection;
